import { useCallback, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { ChevronLeft, ChevronRight, Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { viewableKind } from './viewable-kind';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

export interface FileViewerContentProps {
  fileName: string;
  mimeType?: string | null | undefined;
  url: string | undefined;
  isLoading?: boolean;
  isError?: boolean;
  className?: string;
}

export function FileViewerContent({
  fileName,
  mimeType,
  url,
  isLoading = false,
  isError = false,
  className,
}: Readonly<FileViewerContentProps>) {
  return (
    <div className={cn('min-h-full', className)}>
      <ViewerBody
        fileName={fileName}
        mimeType={mimeType}
        url={url}
        isLoading={isLoading}
        isError={isError}
      />
    </div>
  );
}

function ViewerBody({
  fileName,
  mimeType,
  url,
  isLoading,
  isError,
}: Readonly<{
  fileName: string;
  mimeType: string | null | undefined;
  url: string | undefined;
  isLoading: boolean;
  isError: boolean;
}>) {
  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="text-muted-foreground size-6 animate-spin" />
      </div>
    );
  }
  if (isError || url === undefined) {
    return <ViewerFallback url={url} message="Couldn't load a preview for this file." />;
  }

  const kind = viewableKind(fileName, mimeType);
  if (kind === 'pdf') {
    return <PdfBody url={url} />;
  }
  if (kind === 'image') {
    return <img src={url} alt={fileName} className="mx-auto h-auto max-w-full object-contain" />;
  }
  return <ViewerFallback url={url} message="This file type can't be previewed in the browser." />;
}

function PdfBody({ url }: Readonly<{ url: string }>) {
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [width, setWidth] = useState<number>();

  const containerRef = useCallback((node: HTMLDivElement | null) => {
    if (node !== null) setWidth(node.clientWidth);
  }, []);

  const onLoadSuccess = useCallback(({ numPages: n }: { numPages: number }) => {
    setNumPages(n);
    setPageNumber(1);
  }, []);

  return (
    <div ref={containerRef} className="flex w-full flex-col items-center gap-3">
      <Document
        file={url}
        onLoadSuccess={onLoadSuccess}
        loading={<Loader2 className="text-muted-foreground size-6 animate-spin" />}
        error={<p className="text-muted-foreground p-4 text-sm">Couldn't render this PDF.</p>}
      >
        <Page
          pageNumber={pageNumber}
          {...(width !== undefined ? { width } : {})}
          renderTextLayer
          renderAnnotationLayer={false}
        />
      </Document>

      {numPages > 1 && (
        <PdfPager pageNumber={pageNumber} numPages={numPages} onChange={setPageNumber} />
      )}
    </div>
  );
}

function PdfPager({
  pageNumber,
  numPages,
  onChange,
}: Readonly<{
  pageNumber: number;
  numPages: number;
  onChange: (next: number) => void;
}>) {
  return (
    <div className="flex items-center gap-3">
      <Button
        variant="outline"
        size="icon"
        aria-label="Previous page"
        disabled={pageNumber <= 1}
        onClick={() => {
          onChange(Math.max(1, pageNumber - 1));
        }}
      >
        <ChevronLeft className="size-4" />
      </Button>
      <span className="text-muted-foreground text-sm tabular-nums">
        Page {pageNumber} of {numPages}
      </span>
      <Button
        variant="outline"
        size="icon"
        aria-label="Next page"
        disabled={pageNumber >= numPages}
        onClick={() => {
          onChange(Math.min(numPages, pageNumber + 1));
        }}
      >
        <ChevronRight className="size-4" />
      </Button>
    </div>
  );
}

function ViewerFallback({ url, message }: Readonly<{ url: string | undefined; message: string }>) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
      <p className="text-muted-foreground text-sm">{message}</p>
      <Button variant="outline" asChild={url !== undefined} disabled={url === undefined}>
        {url !== undefined ? (
          <a href={url} target="_blank" rel="noopener noreferrer" download>
            <Download className="size-4" />
            Download
          </a>
        ) : (
          <span>
            <Download className="size-4" />
            Download
          </span>
        )}
      </Button>
    </div>
  );
}
